import React, { PropTypes } from 'react';
import { StickyContainer } from 'react-sticky';
import Collapsible from 'react-collapsible';

import { eventFullPropTypeShape, reviewAbridgedPropType } from '../../proptypes';

import ButtonLink from '../ButtonLink';
import Container from '../Container';
import CourseMaps from '../CourseMaps';
import CourseMapImages from './CourseMapImages';
import EventHeader from '../EventHeader';
import EventListContainer from '../EventList/EventListContainer.react';
import GoogleMap from '../../containers/GoogleMap';
import HelpPrompt from '../HelpPrompt';
import ImageGallery from '../../containers/ImageGallery';
import Labels from './Labels';
import OrganiserReviews from './OrganiserReviews';
import RaceSummary from '../RaceSummary';
import ReadMore from '../ReadMore';
import ReviewList from '../../containers/ReviewList';
import StarRating from '../StarRating';
import Video from './Video';
import VideoPortal from '../VideoPortal';

import { unstyledList } from '../../base-styles/mixins.module.css';

import {
  button,
  buttonNoReviews,
  contentArrow,
  contentBody,
  contentTitle,
  contentTitleOpen,
  courseMapSection,
  descriptionSection,
  keyDetailList,
  header,
  heading,
  headingFullWidth,
  heroImage,
  info,
  organiserReviewStyle,
  moreFromOrganiserFullWidth,
  portalButton,
  portalButtons,
  rating,
  section,
  sectionFullWidth,
  stars,

  // Supported key detail icons
  keyDetail275,
  keyDetail276,
  keyDetail277,
  keyDetail278,
  keyDetail244,
  keyDetail279,
  keyDetail280,
  keyDetail281,
  keyDetail282,
  keyDetail283,
  keyDetail268,
  keyDetail269,
  keyDetail270,
  keyDetail252,
  keyDetail253,
  keyDetail245,
  keyDetail246,
  keyDetail267,
  keyDetailParticipants,
} from './styles.module.css';

const keyDetailIcons = {
  275: keyDetail275,
  276: keyDetail276,
  277: keyDetail277,
  278: keyDetail278,
  244: keyDetail244,
  279: keyDetail279,
  280: keyDetail280,
  281: keyDetail281,
  282: keyDetail282,
  283: keyDetail283,
  268: keyDetail268,
  269: keyDetail269,
  270: keyDetail270,
  252: keyDetail252,
  253: keyDetail253,
  245: keyDetail245,
  246: keyDetail246,
  267: keyDetail267,
  participants: keyDetailParticipants,
};

// These icons require a full row (due to long labels)
const keyDetailRows = [
  279,
  280,
  281,
  282,
  275,
  276,
  278,
  'participants',
];

// This function returns GPX files from event race objects
const racesGPXs = races =>
  races && races.races.map(r => r.gpx ? ({ title: r.title, gpx: r.gpx }) : null).filter(Boolean);

// Filter key details from the first race by supported icons list
const keyDetails = races => races && (races.races[0].courseDetails &&
  races.races[0].courseDetails.filter(d => Object.keys(keyDetailIcons).includes(d.id.toString()))
    .concat(races.races[0].competitorSize ? { id: 'participants', label: `${races.races[0].competitorSize} participants` } : null).filter(Boolean)) || [];

/**
 * Event detail presentational component.
 */
const EventDetail = ({
  description,
  id,
  images,
  flagship,
  location,
  onShare,
  organiser,
  organiserReviews,
  permalink,
  races,
  reliveId,
  reviews,
  searchQuery,
  similarQuery,
  source,
  startDate,
  startDateFormatted,
  title,
  videos,
}) => (
  <div>
    <div style={{ backgroundImage: `url(${images.hero.xlarge})` }} className={heroImage}>
      <Container>
        <div className={portalButtons}>
          {videos && (
            <div>
              <VideoPortal src={videos[0].url} className={portalButton} text="Video" />
            </div>
          )}
          {images && images.gallery && (
            <ImageGallery
              images={images.gallery.map(img => ({ src: img.large }))}
              className={portalButton}
              buttonLabel="Photos"
            />
          )}
        </div>
      </Container>
    </div>

    <StickyContainer>
      <header className={header}>
        <EventHeader
          id={id}
          title={title}
          organiser={organiser}
          startDate={startDate}
          startDateFormatted={startDateFormatted}
          location={location && location.address && location.address.join(', ')}
          races={races}
          reviews={reviews}
          permalink={permalink}
          source={source}
          onShare={onShare}
        />
      </header>

      <div style={{ position: 'relative' }}>
        <section className={descriptionSection}>
          <Container>
            {((races && keyDetails(races).length > 1) || description) && (
              <div className={info}>
                <div>
                  {races && keyDetails(races).length > 1 && (
                    <div>
                      <ul className={keyDetailList}>
                        {keyDetails(races).map(keyDetail => (
                          <li
                            key={keyDetail.id}
                            style={{
                              width: keyDetailRows.includes(keyDetail.id) ? '100%' : '50%',
                            }}
                          >
                            <span className={keyDetailIcons[keyDetail.id]} />
                            <span>{keyDetail.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {description && (
                    <div>
                      <h2 className={heading}>What you need to know</h2>
                      <ReadMore content={description} lineCount={6} html />
                    </div>
                  )}
                </div>

                {reliveId && (
                  <div>
                    <Video reliveId={reliveId} />
                  </div>
                )}
              </div>
            )}
          </Container>
        </section>

        {races && races.races.length > 1 && (
          <section className={section}>
            <Container>
              <div className={info}>
                <h2 className={heading}>{'Distances in this event'}</h2>
                <ol className={unstyledList}>
                  {races.races.map(race => (
                    <li key={race.id}>
                      <RaceSummary race={race} />
                    </li>
                  ))}
                </ol>
              </div>
            </Container>
          </section>
        )}
      </div>

      {races && racesGPXs(races).length > 0 &&
        <section className={courseMapSection}>
          <Container>
            <div className={info}>
              <h2 className={heading}>Route info</h2>
              <CourseMaps courses={racesGPXs(races)} showReliveLink={!!reliveId} />
            </div>
          </Container>
        </section>}

      <section className={section} id="races">
        <Container>
          <div className={info}>
            {races && races.races[0].courseMaps && (
              <Collapsible
                transitionTime={100}
                transitionCloseTime={100}
                triggerClassName={contentTitle}
                triggerOpenedClassName={contentTitleOpen}
                trigger={
                  <div>
                    {'Course Maps'}
                    <span className={contentArrow} />
                  </div>
                }
              >
                <div style={{ padding: '20px 15px' }}>
                  <CourseMapImages images={races.races[0].courseMaps} />
                </div>
              </Collapsible>
            )}
            {races && races.races[0].customContent && races.races[0].customContent.map(content => (
              <Collapsible
                key={content.key}
                transitionTime={100}
                transitionCloseTime={100}
                triggerClassName={contentTitle}
                triggerOpenedClassName={contentTitleOpen}
                trigger={
                  <div>
                    {content.label}
                    <span className={contentArrow} />
                  </div>
                }
              >
                <div
                  dangerouslySetInnerHTML={{ __html: content.content }}
                  className={contentBody}
                />
              </Collapsible>
            ))}
            <Collapsible
              transitionTime={100}
              transitionCloseTime={100}
              triggerClassName={contentTitle}
              triggerOpenedClassName={contentTitleOpen}
              open={!races || (!races.races[0].courseMaps && !races.races[0].customContent)}
              trigger={
                <div>
                  {'View on a map'}
                  <span className={contentArrow} />
                </div>
              }
            >
              <div style={{ padding: '20px 0' }}>
                <GoogleMap lat={location.lat} lng={location.long} />
              </div>
            </Collapsible>
          </div>
        </Container>
      </section>

      <section className={section} id="reviews">
        <Container>
          <div className={info}>
            {reviews && (
              <div>
                <StarRating className={stars} value={reviews.ratings.overall} />
                <h2 className={heading}>{reviews.totalFormatted} {reviews.total > 1 ? 'Reviews' : 'Review'}</h2>
                <ul className={unstyledList}>
                  <li className={rating}>
                    Overall
                    <StarRating className={stars} value={reviews.ratings.overall} />
                  </li>
                  <li className={rating}>
                    Course
                    <StarRating className={stars} value={reviews.ratings.course} />
                  </li>
                  <li className={rating}>
                    Organisation
                    <StarRating className={stars} value={reviews.ratings.organisation} />
                  </li>
                  <li className={rating}>
                    Atmosphere
                    <StarRating className={stars} value={reviews.ratings.atmosphere} />
                  </li>
                </ul>
              </div>
            )}
            {!reviews &&
              <div>
                <h2 className={heading}>Reviews</h2>
                <p>{'There are no reviews yet for this event. Raced here before? Be the first to review this event!'}</p>
              </div>}
            <ButtonLink
              to={`${permalink}/review`}
              rel="nofollow"
              className={reviews ? button : buttonNoReviews}
            >
              Write A Review
            </ButtonLink>
            {reviews && <ReviewList eventId={id} />}
          </div>
        </Container>
      </section>
    </StickyContainer>


    {races && !races.bookingPartner && !flagship &&
    <div>
      <section className={section}>
        <Container>
          <h2 className={headingFullWidth}>Similar Events</h2>
          {similarQuery &&
          <EventListContainer
            query={similarQuery}
            showLoadMore={false}
            source="event_page"
            carousel
          />}
        </Container>
      </section>
    </div>}

    {id !== 84206 && (
      <div>
        {((races && races.bookingPartner) || flagship) &&
          <section className={sectionFullWidth}>
            <Container>
              <h2 className={headingFullWidth}>Similar Events</h2>
              {similarQuery &&
              <EventListContainer
                query={similarQuery}
                showLoadMore={false}
                source="event_page"
                carousel
              />}
            </Container>
          </section>}

        {organiserReviews &&
          <section>
            <Container className={organiserReviewStyle}>
              <h2 className={headingFullWidth}>Organiser Reviews</h2>
              <OrganiserReviews organiserReviews={organiserReviews} />
            </Container>
          </section>}
        {organiser &&
        <section className={moreFromOrganiserFullWidth}>
          <Container>
            <h2 className={headingFullWidth}>More from {organiser.title}</h2>
            {searchQuery &&
            <EventListContainer
              query={searchQuery}
              showLoadMore={false}
              source="event_page"
              carousel
            />}
          </Container>
        </section>}
      </div>
    )}

    <section className={section}>
      <Container>
        <Labels races={races} />
      </Container>
    </section>

    <HelpPrompt
      title="Not sure if this race is for you?"
      message="Got questions about training? Our team are here to help."
    />
  </div>
);

EventDetail.propTypes = {
  /**
   * HTML string description of event.
   */
  description: eventFullPropTypeShape.description,
  /**
   * Object containing event latlng location and address.
   */
  location: eventFullPropTypeShape.location,
  /**
   * Numeric ID of the event.
   */
  id: eventFullPropTypeShape.id, // eslint-disable-line react/require-default-props
  /**
   * Object containing image data objects.
   */
  images: eventFullPropTypeShape.images,
   /**
   * Boolean as to wether the event is a flagship.
   */
  flagship: eventFullPropTypeShape.flagship,
  /**
   * Tracking function called when user clicks on 'Share' button.
   */
  onShare: PropTypes.func,
  /**
   * Organiser data object.
   */
  organiser: eventFullPropTypeShape.organiser,
  /**
   * String permalink to the event page.
   */
  permalink: eventFullPropTypeShape.permalink, // eslint-disable-line react/require-default-props
  /**
   * Array of race objects.
   */
  races: eventFullPropTypeShape.races,
  /**
   * String ID of the relive video.
   */
  reliveId: PropTypes.string,
  /**
   * Object containing event reviews.
   */
  reviews: eventFullPropTypeShape.reviews,
  /**
   * Object containing organiser reviews.
   */
  organiserReviews: PropTypes.arrayOf(reviewAbridgedPropType.isRequired),
  /**
   * Query object for 'search' query.
   */
  searchQuery: PropTypes.shape(),
  /**
   * Query object for 'similar' query.
   */
  similarQuery: PropTypes.shape(),
  /**
   * Optional string source.
   */
  source: PropTypes.string,
  /**
   * String ISO timestamp of event start date.
   */
  startDate: eventFullPropTypeShape.startDate,
  /**
   * Formatted string of race start date.
   */
  startDateFormatted: eventFullPropTypeShape.startDateFormatted,
  /**
   * String title of the event.
   */
  title: eventFullPropTypeShape.title, // eslint-disable-line react/require-default-props
  /**
   * Object containing video data objects.
   */
  videos: eventFullPropTypeShape.videos,
};

EventDetail.defaultProps = {
  description: undefined,
  flagship: undefined,
  location: undefined,
  images: undefined,
  onShare: () => {},
  organiser: undefined,
  organiserReviews: undefined,
  races: undefined,
  reliveId: undefined,
  reviews: undefined,
  searchQuery: {},
  similarQuery: {},
  source: undefined,
  startDate: undefined,
  startDateFormatted: undefined,
  videos: undefined,
};

export default EventDetail;



// WEBPACK FOOTER //
// ./app/src/components/EventDetail/index.jsx